// Extra feature sections built from help center articles.
export const MORE_RUN = {
    "booking-scheduling": {
        tabLinks: {
            "live-availability": "/help-center/knowledge-base/calendar/navigate-the-calendar#change-the-view",
            "smart-reminders": "/help-center/knowledge-base/calendar/send-appointment-reminders#set-up-reminders",
            "team-visibility": "/help-center/knowledge-base/calendar/navigate-the-calendar#use-the-day-panel",
            "rebooking-flow": "/help-center/knowledge-base/calendar/complete-appointments#check-out-an-appointment",
        },
        sections: [
            {
                type: "row",
                title: "Book in a few clicks",
                description: "Click any open slot, choose the client and service, and the appointment is on the calendar. Walk-ins can be booked without a client profile.",
                image: "calendar/appointment-new-panel",
                href: "/help-center/knowledge-base/calendar/create-appointments#book-a-specific-time-slot",
                linkLabel: "Book an appointment",
            },
            {
                type: "group",
                heading: "Keep your chairs full, week after week",
                cards: [
                    {
                        title: "Recurring visits, booked once",
                        description: "Book regular clients into the same slot every day, week or month in one go.",
                        image: "calendar/appointment-repeat",
                        href: "/help-center/knowledge-base/calendar/create-repeat-appointments#create-a-repeat-appointment",
                        linkLabel: "Set up repeat bookings",
                    },
                    {
                        title: "Fill cancellations from your waitlist",
                        description: "Keep a list of clients waiting for a slot, then turn an entry into an appointment or drag it straight onto the calendar.",
                        image: "calendar/waitlist-panel",
                        href: "/help-center/knowledge-base/calendar/manage-waitlist-entries#book-a-waitlist-entry",
                        linkLabel: "See how waitlists work",
                    },
                ],
            },
            {
                type: "row",
                title: "Reschedule with drag and drop",
                description: "Drag an appointment to a new time or team member. Choose whether clients are asked about, always told or never told about the change.",
                image: "calendar/appointment-dragging",
                href: "/help-center/knowledge-base/calendar/reschedule-appointments#drag-and-drop",
                linkLabel: "Move an appointment",
            },
            {
                type: "row",
                title: "Approve online bookings your way",
                description: "Auto approve every booking, only existing clients, or none. Unconfirmed bookings stand out on the calendar so you can accept or decline them in a click.",
                image: "calendar/appointment-unconfirmed-hover",
                href: "/help-center/knowledge-base/calendar/update-appointment-statuses#accept-or-decline-unconfirmed-appointments",
                linkLabel: "Review booking requests",
            },
            {
                type: "group",
                heading: "Protect your time and your calendar",
                cards: [
                    {
                        title: "Block time for breaks and training",
                        description: "Reserve time for lunch, training or admin so it can't be booked, using reusable block time types.",
                        image: "calendar/block-time-new",
                        href: "/help-center/knowledge-base/calendar/create-and-manage-blocked-time#block-time",
                        linkLabel: "Block off time",
                    },
                    {
                        title: "Cancellation policies that work for you",
                        description: "Let clients reschedule or cancel online within your window, ask them to accept your policy, and set a late cancellation fee.",
                        image: "calendar/booking-control",
                        href: "/help-center/knowledge-base/calendar/set-rescheduling-and-cancellation-policies#update-booking-control",
                        linkLabel: "Set my policies",
                    },
                ],
            },
            {
                type: "row",
                title: "Group bookings made simple",
                description: "Book several clients together, each with their own services and team member, then check everyone out in one sale.",
                image: "calendar/group-on-calendar",
                href: "/help-center/knowledge-base/calendar/create-group-appointments#book-a-group",
                linkLabel: "Book a group",
            },
            {
                type: "row",
                title: "Know who changed what",
                description: "Every change to an appointment is recorded with who made it and when, so there's never any confusion at the front desk.",
                image: "calendar/appointment-change-history",
                href: "/help-center/knowledge-base/calendar/update-appointments#view-change-history",
                linkLabel: "View change history",
            },
        ],
    },

    "staff-management": {
        tabLinks: {
            "roles-access": "/help-center/knowledge-base/team/manage-roles-and-permissions#change-one-team-members-permissions",
            "shift-planning": "/help-center/knowledge-base/calendar/manage-time-off#add-time-off",
            commissions: "/help-center/knowledge-base/team/set-up-commissions#set-default-rules",
            performance: "/help-center/knowledge-base/reports/performance-dashboard#filter-the-dashboard",
        },
        sections: [
            {
                type: "row",
                title: "Your whole team, one list",
                description: "Give every team member a profile, calendar and login. Track who's active, invited or archived at a glance.",
                image: "team/team-members-list",
                href: "/help-center/knowledge-base/team/add-team-members#add-a-team-member",
                linkLabel: "Add team members",
            },
            {
                type: "group",
                heading: "Manage time off and scheduling on one platform",
                cards: [
                    {
                        title: "Working hours that drive your calendar",
                        description: "Set each team member's weekly hours and whether they can be booked online, so clients only see real availability.",
                        image: "calendar/schedule-manage",
                        href: "/help-center/knowledge-base/calendar/manage-team-schedules#edit-a-schedule",
                        linkLabel: "Set team schedules",
                    },
                    {
                        title: "PTO without the back-and-forth",
                        description: "Create your own time off types and mark each one as paid or unpaid, so everyone knows how absences are handled.",
                        image: "calendar/time-off-types",
                        href: "/help-center/knowledge-base/calendar/manage-time-off-types#add-a-time-off-type",
                        linkLabel: "Set up time off types",
                    },
                ],
            },
            {
                type: "row",
                title: "A time clock in every login",
                description: "Team members clock in, take breaks and clock out right from Zobliss. Every shift lands in timesheets for approval.",
                image: "team/time-clock-menu",
                href: "/help-center/knowledge-base/team/clock-in-and-out#use-the-time-clock",
                linkLabel: "Start tracking time",
            },
            {
                type: "row",
                title: "Timesheets you can trust",
                description: "Approve, flag or edit shifts before pay day. Every manual entry and edit needs a reason and is saved in the audit log.",
                image: "team/timesheets",
                href: "/help-center/knowledge-base/team/manage-timesheets#review-timesheets",
                linkLabel: "Review timesheets",
            },
            {
                type: "group",
                heading: "Pay your team accurately, every period",
                cards: [
                    {
                        title: "Every earning in one table",
                        description: "See each team member's hours, wages, PTO, commission and tips for any period, and add adjustments when needed.",
                        image: "team/earnings",
                        href: "/help-center/knowledge-base/team/view-team-earnings#view-earnings",
                        linkLabel: "View team earnings",
                    },
                    {
                        title: "Payroll that runs itself",
                        description: "Connect Gusto and push hours, tips and commissions at the end of each pay period. Gusto pays your team and files payroll taxes.",
                        image: "team/payroll-gusto",
                        href: "/help-center/knowledge-base/team/run-payroll-with-gusto#connect-gusto",
                        linkLabel: "Connect Gusto",
                    },
                ],
            },
            {
                type: "row",
                title: "Roles ready from day one",
                description: "Start with Owner, Manager, Staff, Receptionist and Accountant roles, then fine-tune what each role can see and do.",
                image: "team/team-role-roles",
                href: "/help-center/knowledge-base/team/manage-roles-and-permissions#change-permissions",
                linkLabel: "Customize roles",
            },
            {
                type: "row",
                title: "Goodbyes without losing history",
                description: "Archive team members who've left after reassigning their upcoming appointments. Their past sales and reviews stay in your reports.",
                image: "team/team-member-archive",
                href: "/help-center/knowledge-base/team/archive-team-members#archive-a-team-member",
                linkLabel: "Archive a team member",
            },
        ],
    },

    sales: {
        tabLinks: {
            "daily-dashboard": "/help-center/knowledge-base/sales/view-sales-history#find-a-sale",
            "service-mix": "/help-center/knowledge-base/reports/sales-report#what-it-shows",
            "staff-sales": "/help-center/knowledge-base/team/view-team-earnings#view-earnings",
            "exportable-reporting": "/help-center/knowledge-base/reports/share-and-export-reports#print-or-download",
        },
        sections: [
            {
                type: "row",
                title: "Sell without a booking",
                description: "Ring up services, products and packages for walk-in clients in a quick sale, no appointment needed.",
                image: "sales/walk-in-sale-panel",
                href: "/help-center/knowledge-base/sales/create-a-quick-sale#start-a-quick-sale",
                linkLabel: "Start a quick sale",
            },
            {
                type: "group",
                heading: "A checkout that keeps clients happy",
                cards: [
                    {
                        title: "Discounts in a tap",
                        description: "Take an amount or a percentage off the whole sale, or change the price of a single item.",
                        image: "sales/sale-cart-discount",
                        href: "/help-center/knowledge-base/sales/apply-discounts-at-checkout#apply-a-cart-discount",
                        linkLabel: "Apply a discount",
                    },
                    {
                        title: "Rewards applied at checkout",
                        description: "See the rewards a client can use and take them off the sale before they pay.",
                        image: "sales/sale-apply-reward",
                        href: "/help-center/knowledge-base/sales/apply-rewards-at-checkout#apply-a-reward",
                        linkLabel: "Redeem a reward",
                    },
                ],
            },
            {
                type: "row",
                title: "Take payment your way",
                description: "Accept card, cash and gift card payments, or record a payment taken outside Zobliss. Card payments work with Stripe or Square.",
                image: "sales/sale-pay-now",
                href: "/help-center/knowledge-base/sales/take-payments-at-checkout#payment-methods",
                linkLabel: "Take a payment",
            },
            {
                type: "row",
                title: "Split tips fairly",
                description: "When more than one team member works on a sale, split the tip between them with a custom amount for each.",
                image: "sales/sale-tip-split",
                href: "/help-center/knowledge-base/sales/set-up-and-manage-tips#split-a-tip",
                linkLabel: "Split a tip",
            },
            {
                type: "group",
                heading: "Stay on top of every dollar",
                cards: [
                    {
                        title: "Refunds without the headache",
                        description: "Refund all or part of a sale back to the original payment method or in cash.",
                        image: "sales/sale-refund",
                        href: "/help-center/knowledge-base/sales/refund-a-sale#issue-a-refund",
                        linkLabel: "Issue a refund",
                    },
                    {
                        title: "Invoices you can follow",
                        description: "See which invoices are draft, awaiting payment, past due or paid, and send, edit or print them from one menu.",
                        image: "sales/invoice-actions-menu",
                        href: "/help-center/knowledge-base/sales/manage-invoices#invoice-statuses",
                        linkLabel: "Track my invoices",
                    },
                ],
            },
            {
                type: "row",
                title: "Taxes added automatically",
                description: "Set a tax rate for services and one for products at each location. Zobliss adds them to every checkout, quick sale and invoice.",
                image: "sales/tip-tax-settings",
                href: "/help-center/knowledge-base/sales/set-up-taxes#set-tax-rates",
                linkLabel: "Set up taxes",
            },
            {
                type: "row",
                title: "Receipts, printed or emailed",
                description: "Print a receipt at the front desk or email it to the client, and send receipts automatically after every payment.",
                image: "sales/sale-payment-details",
                href: "/help-center/knowledge-base/sales/share-and-print-receipts#send-a-receipt",
                linkLabel: "Send a receipt",
            },
        ],
    },

    "multi-location": {
        tabLinks: {
            "unified-account": "/help-center/knowledge-base/workspace-settings/manage-locations#view-locations",
            "location-teams": "/help-center/knowledge-base/team/update-team-member-details#assign-locations",
            "location-taxes": "/help-center/knowledge-base/team/set-up-team-compensation#set-compensation",
            "cross-location-reporting": "/help-center/knowledge-base/reports/performance-dashboard#filter-the-dashboard",
        },
        sections: [
            {
                type: "row",
                title: "Add a location when you're ready",
                description: "Open a new location without changing your plan. The Multi location add-on is billed monthly per location and can be adjusted any time.",
                image: "billing-and-fees/billing-add-ons",
                href: "/help-center/knowledge-base/billing-and-fees/add-locations-and-team-members#available-add-ons",
                linkLabel: "Add a location",
            },
            {
                type: "group",
                heading: "Run every location its own way",
                cards: [
                    {
                        title: "Booking rules per location",
                        description: "Turn online booking and the waitlist on or off, choose auto approval and set the booking notice for each location.",
                        image: "calendar/location-booking-settings",
                        href: "/help-center/knowledge-base/calendar/manage-online-booking-settings#booking-settings-explained",
                        linkLabel: "Set booking rules",
                    },
                    {
                        title: "Tax and tips by location",
                        description: "Each location has its own tax rates and tip options, added automatically at checkout there.",
                        image: "sales/tip-tax-settings",
                        href: "/help-center/knowledge-base/sales/set-up-taxes#set-tax-rates",
                        linkLabel: "Set location taxes",
                    },
                ],
            },
            {
                type: "row",
                title: "Switch locations from the calendar",
                description: "Use the location dropdown on the calendar to jump between locations and see each team's day.",
                image: "calendar/calendar-bottom-bar",
                href: "/help-center/knowledge-base/calendar/navigate-the-calendar#filter-team-members-and-resources",
                linkLabel: "Unify my calendar",
            },
            {
                type: "row",
                title: "Stock counted at every location",
                description: "Zobliss keeps a stock count for each product at each location and lowers it automatically when the product is sold.",
                image: "inventory/inventory-table",
                href: "/help-center/knowledge-base/inventory/track-stock-levels#view-stock",
                linkLabel: "Track stock by location",
            },
            {
                type: "group",
                heading: "See how each location is really doing",
                cards: [
                    {
                        title: "Reports in local time",
                        description: "Filter any report by location. Dates follow each location's time zone, so a late sale counts on the day it happened there.",
                        image: "reports/report-filters",
                        href: "/help-center/knowledge-base/reports/run-and-filter-reports#how-filters-work",
                        linkLabel: "Filter reports",
                    },
                    {
                        title: "Sales history by location",
                        description: "Find any completed sale and filter by location, status and date to check each location's takings.",
                        image: "sales/sales-history",
                        href: "/help-center/knowledge-base/sales/view-sales-history#find-a-sale",
                        linkLabel: "Find a sale",
                    },
                ],
            },
            {
                type: "row",
                title: "A booking link for every location",
                description: "Share a link that opens straight to booking for one location, great for each location's website or social profile.",
                image: "online-presence/link-builder",
                href: "/help-center/knowledge-base/online-presence/create-booking-links#link-types",
                linkLabel: "Create a location link",
            },
        ],
    },

    "resource-management": {
        tabLinks: {
            "rooms-spaces": "/help-center/knowledge-base/calendar/manage-bookable-resources#add-a-resource",
            "shared-equipment": "/help-center/knowledge-base/calendar/manage-bookable-resources#link-resources-to-services",
            "live-availability": "/help-center/knowledge-base/calendar/manage-bookable-resources#view-resources-on-the-calendar",
        },
        sections: [
            {
                type: "row",
                title: "Organize resources by type",
                description: "Group your resources into types like rooms, chairs, wash stations and equipment, then add each one with its own capacity limit.",
                image: "calendar/resources-add-menu",
                href: "/help-center/knowledge-base/calendar/manage-bookable-resources#add-a-resource",
                linkLabel: "Add a resource",
            },
            {
                type: "group",
                heading: "Link resources to the services that need them",
                cards: [
                    {
                        title: "Services that reserve their own space",
                        description: "Turn on Require resources and choose the rooms or equipment a service needs at each location. Services like a sauna can run without a team member.",
                        image: "services/service-options",
                        href: "/help-center/knowledge-base/services/manage-service-availability#resources",
                        linkLabel: "Link resources",
                    },
                    {
                        title: "Time to reset between clients",
                        description: "Add block time after a service for clean-up, checkout or travel, so the next appointment doesn't start too soon.",
                        image: "services/service-pricing-duration",
                        href: "/help-center/knowledge-base/services/set-pricing-and-durations#duration",
                        linkLabel: "Add clean-up time",
                    },
                ],
            },
            {
                type: "row",
                title: "Team and rooms side by side",
                description: "Filter the calendar to all resources, or all team and resources, to see a column for every room, chair and machine.",
                image: "calendar/calendar-team-filter",
                href: "/help-center/knowledge-base/calendar/navigate-the-calendar#filter-team-members-and-resources",
                linkLabel: "Filter the calendar",
            },
            {
                type: "row",
                title: "No more double-booked rooms",
                description: "When a service requires a resource, Zobliss only books it when that resource is free, so two clients never land in the same room.",
                image: "calendar/book-appointment-panel",
                href: "/help-center/knowledge-base/calendar/manage-bookable-resources",
                linkLabel: "See how it works",
            },
            {
                type: "group",
                heading: "Set up resources for every location",
                cards: [
                    {
                        title: "Resources where you need them",
                        description: "Choose the location each resource can be booked at and limit when it's available.",
                        image: "workspace-settings/locations-list",
                        href: "/help-center/knowledge-base/calendar/manage-bookable-resources#add-a-resource",
                        linkLabel: "Add a resource",
                    },
                    {
                        title: "Switch locations in a click",
                        description: "Use the calendar's team filter and location dropdown to check rooms and team members at each location.",
                        image: "calendar/calendar-bottom-bar",
                        href: "/help-center/knowledge-base/calendar/navigate-the-calendar#filter-team-members-and-resources",
                        linkLabel: "Navigate the calendar",
                    },
                ],
            },
        ],
    },

    reports: {
        tabLinks: {
            "performance-dashboard": "/help-center/knowledge-base/reports/performance-dashboard#whats-on-the-dashboard",
            "sales-finance": "/help-center/knowledge-base/reports/reports-overview#sales-and-finance-reports",
            "filters-exports": "/help-center/knowledge-base/reports/run-and-filter-reports#how-filters-work",
            "report-access": "/help-center/knowledge-base/reports/manage-report-access#choose-who-can-see-reports",
        },
        sections: [
            {
                type: "row",
                title: "Focus on one team member",
                description: "Filter the dashboard by location or team member to see only their sales, appointments and working hours.",
                image: "reports/performance-dashboard-filter-chips",
                href: "/help-center/knowledge-base/reports/performance-dashboard#filter-the-dashboard",
                linkLabel: "Filter the dashboard",
            },
            {
                type: "group",
                heading: "Know exactly where your money comes from",
                cards: [
                    {
                        title: "Every line item, every tip",
                        description: "See every service, add-on, product and tip on every sale, and who it was credited to.",
                        image: "reports/report-transaction-detail",
                        href: "/help-center/knowledge-base/reports/transaction-detail-report#what-it-shows",
                        linkLabel: "Open transaction detail",
                    },
                    {
                        title: "Tax collected, sale by sale",
                        description: "List every sale with tax in your date range, with its items, tax total and client.",
                        image: "reports/report-sales-tax",
                        href: "/help-center/knowledge-base/reports/sales-tax-report#what-it-shows",
                        linkLabel: "Check sales tax",
                    },
                ],
            },
            {
                type: "row",
                title: "Spot cancellations and no-shows",
                description: "Review your appointment history by date, client, team member and status to see how busy each team member and location was.",
                image: "reports/report-appointments",
                href: "/help-center/knowledge-base/reports/appointments-report#what-it-shows",
                linkLabel: "View appointments report",
            },
            {
                type: "row",
                title: "Meet your best clients",
                description: "See each team member's top 10 clients by amount spent and by number of appointments.",
                image: "reports/report-most-valuable-clients",
                href: "/help-center/knowledge-base/reports/most-valuable-clients-report#what-it-shows",
                linkLabel: "Find top clients",
            },
            {
                type: "group",
                heading: "Track products, packages and gift cards",
                cards: [
                    {
                        title: "Retail profit by product",
                        description: "See units sold, sales and profit for each product, and which team member sold it.",
                        image: "reports/report-inventory",
                        href: "/help-center/knowledge-base/reports/inventory-report#what-it-shows",
                        linkLabel: "See product profit",
                    },
                    {
                        title: "Know what's left on gift cards",
                        description: "See the balance on every gift card and the total outstanding liability across your business.",
                        image: "reports/report-gift-card-balances",
                        href: "/help-center/knowledge-base/reports/gift-card-balances-report#what-it-shows",
                        linkLabel: "Check gift card balances",
                    },
                ],
            },
            {
                type: "row",
                title: "Every report in one place",
                description: "Search reports by name or browse by category, from dashboards and sales to clients and inventory.",
                image: "reports/reports-list",
                href: "/help-center/knowledge-base/reports/reports-overview#find-a-report",
                linkLabel: "Browse all reports",
            },
            {
                type: "row",
                title: "Lock reports on the front desk",
                description: "Set a 4 digit PIN so team members on a shared device can't open Reports or Payouts from the calendar without it.",
                image: "team/front-desk-pin",
                href: "/help-center/knowledge-base/team/set-a-front-desk-pin#set-the-pin",
                linkLabel: "Set a front desk PIN",
            },
        ],
    },
};
